/**
 * Звук и вибрация. Весь звук синтезируется (Web Audio): сэмплов нет, грузить и
 * кешировать для офлайна нечего.
 *
 * Вибрация — через navigator.vibrate; в Capacitor-сборке (Фаза 5) заменить её
 * здесь на @capacitor/haptics, остальной код про это не знает.
 */

export interface Feedback {
  /**
   * Вызывать из обработчика жеста (клик «Играть»): браузеры не дают
   * AudioContext звучать, пока пользователь ничего не нажал.
   */
  unlock(): void
  hit(perfect: boolean, combo: number): void
  miss(): void
  /**
   * Пойман бонус: монета — короткий «дзынь», хороший эффект — восходящее
   * арпеджио, отрицательный — низкий нисходящий «бз-з».
   */
  bonus(kind: 'coin' | 'good' | 'bad'): void
  /** Страховка спасла от промаха. */
  shield(): void
  setMuted(muted: boolean): void
}

export function createFeedback(muted: boolean): Feedback {
  let ctx: AudioContext | null = null
  let noise: AudioBuffer | null = null
  let isMuted = muted

  function audio(): AudioContext | null {
    if (isMuted || !ctx || ctx.state !== 'running') return null
    return ctx
  }

  function vibrate(pattern: number | number[]): void {
    try {
      navigator.vibrate?.(pattern)
    } catch {
      // Нет API (десктоп, iOS Safari) или запрещено политикой — молча без вибрации.
    }
  }

  return {
    unlock() {
      if (typeof AudioContext === 'undefined') return
      ctx ??= new AudioContext()
      if (!noise) noise = makeNoise(ctx)
      if (ctx.state === 'suspended') void ctx.resume()
    },

    hit(perfect, combo) {
      vibrate(perfect ? 18 : 12)
      const ac = audio()
      if (!ac || !noise) return
      // Серия поднимает тон — слышно, что темп держится.
      const pitch = 1 + Math.min(combo, 10) * 0.03
      playThock(ac, noise, (perfect ? 2400 : 1800) * pitch)
    },

    miss() {
      vibrate([40, 60, 80])
      const ac = audio()
      if (ac) playDrop(ac)
    },

    bonus(kind) {
      vibrate(kind === 'coin' ? 8 : kind === 'good' ? 20 : [30, 20, 30])
      const ac = audio()
      if (!ac) return
      if (kind === 'bad') playBuzz(ac)
      else playChime(ac, kind === 'coin' ? [1320, 1760] : [660, 880, 1320])
    },

    shield() {
      vibrate([15, 30, 15])
      const ac = audio()
      if (ac) playChime(ac, [440, 330, 660])
    },

    setMuted(value) {
      isMuted = value
    },
  }
}

function makeNoise(ac: AudioContext): AudioBuffer {
  const buffer = ac.createBuffer(1, Math.floor(ac.sampleRate * 0.1), ac.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
  return buffer
}

/** Удар по струнам: щелчок полосового шума + короткий низкий «тук» корпуса. */
function playThock(ac: AudioContext, noise: AudioBuffer, freq: number): void {
  const t = ac.currentTime

  const src = ac.createBufferSource()
  src.buffer = noise
  const band = ac.createBiquadFilter()
  band.type = 'bandpass'
  band.frequency.value = freq
  band.Q.value = 1.2
  const noiseGain = ac.createGain()
  noiseGain.gain.setValueAtTime(0.5, t)
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.06)
  src.connect(band).connect(noiseGain).connect(ac.destination)
  src.start(t)
  src.stop(t + 0.08)

  const osc = ac.createOscillator()
  osc.frequency.setValueAtTime(180, t)
  osc.frequency.exponentialRampToValueAtTime(90, t + 0.08)
  const oscGain = ac.createGain()
  oscGain.gain.setValueAtTime(0.35, t)
  oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.09)
  osc.connect(oscGain).connect(ac.destination)
  osc.start(t)
  osc.stop(t + 0.1)
}

/** Промах: мягкий нисходящий тон, без резкости — это таймкиллер, не наказание. */
function playDrop(ac: AudioContext): void {
  const t = ac.currentTime
  const osc = ac.createOscillator()
  osc.type = 'triangle'
  osc.frequency.setValueAtTime(330, t)
  osc.frequency.exponentialRampToValueAtTime(110, t + 0.45)
  const gain = ac.createGain()
  gain.gain.setValueAtTime(0.25, t)
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5)
  osc.connect(gain).connect(ac.destination)
  osc.start(t)
  osc.stop(t + 0.55)
}

/** Короткие синусные ноты подряд — звук «награды», мягче удара. */
function playChime(ac: AudioContext, notes: number[]): void {
  const t0 = ac.currentTime
  notes.forEach((freq, i) => {
    const t = t0 + i * 0.07
    const osc = ac.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(freq, t)
    const gain = ac.createGain()
    gain.gain.setValueAtTime(0.0001, t)
    gain.gain.exponentialRampToValueAtTime(0.18, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22)
    osc.connect(gain).connect(ac.destination)
    osc.start(t)
    osc.stop(t + 0.25)
  })
}

/** Отрицательный бонус: короткий низкий «бз-з» с уходом вниз. */
function playBuzz(ac: AudioContext): void {
  const t = ac.currentTime
  const osc = ac.createOscillator()
  osc.type = 'sawtooth'
  osc.frequency.setValueAtTime(220, t)
  osc.frequency.exponentialRampToValueAtTime(110, t + 0.25)
  const filter = ac.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 900
  const gain = ac.createGain()
  gain.gain.setValueAtTime(0.0001, t)
  gain.gain.exponentialRampToValueAtTime(0.14, t + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3)
  osc.connect(filter).connect(gain).connect(ac.destination)
  osc.start(t)
  osc.stop(t + 0.32)
}
